import { useState } from "react";
import { Search, Globe, ChevronRight, Check, LocateFixed } from "lucide-react";
import {
  availableTimezones,
  commonZones,
  detectTimezone,
  timezoneLabel,
  timezoneLocation,
  timezoneOffset,
} from "../../shared/timezones";
import { useStore } from "../store";
import { Modal, ErrorText } from "./ui";
export function TimezoneSelector({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => Promise<void>;
}) {
  const { offline } = useStore();
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false),
    [search, setSearch] = useState(""),
    [saving, setSaving] = useState(false);
  const zones = availableTimezones(value),
    common = new Set<string>(commonZones.map(([zone]) => zone));
  const matching = zones.filter((zone) =>
    (timezoneLocation(zone) + " " + zone + " " + timezoneOffset(zone))
      .toLowerCase()
      .includes(search.toLowerCase().trim()),
  );
  const groups = search
    ? [["Search results", matching] as const]
    : [
        [
          "Common locations",
          matching.filter((zone) => common.has(zone)),
        ] as const,
        ...[
          "Africa",
          "America",
          "Antarctica",
          "Arctic",
          "Asia",
          "Atlantic",
          "Australia",
          "Europe",
          "Indian",
          "Pacific",
        ].map(
          (region) =>
            [
              region === "America" ? "Americas" : region,
              matching.filter(
                (zone) => !common.has(zone) && zone.startsWith(region + "/"),
              ),
            ] as const,
        ),
      ];
  async function choose(zone: string) {
    if (saving || offline) return;
    setError("");
    setSaving(true);
    try {
      await onChange(zone);
      setOpen(false);
    } catch {
      setError(
        "The timezone could not be saved. Check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="timezone-setting">
      <span className="setting-label">
        <Globe size={16} />
        Timezone
      </span>
      <button
        className="timezone-trigger"
        onClick={() => {
          setSearch("");
          setOpen(true);
        }}
        aria-label={`Change timezone, ${timezoneLabel(value)}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>
          <strong>{timezoneLocation(value)}</strong>
          <small>{timezoneOffset(value)} · Adjusts for daylight saving</small>
        </span>
        <ChevronRight size={18} />
      </button>
      <p className="muted timezone-help">
        Appointments keep the same moment in time. Their displayed local time
        changes with the timezone.
      </p>
      {open && (
        <Modal title="Choose timezone" onClose={() => setOpen(false)}>
          <label className="search timezone-search">
            <Search size={18} />
            <input
              autoFocus
              aria-label="Search timezones"
              placeholder="Search city, region or timezone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <button
            className="detect-timezone"
            disabled={saving || offline}
            onClick={() => void choose(detectTimezone())}
          >
            <LocateFixed size={18} />
            <span>
              Use device timezone
              <small>{timezoneLabel(detectTimezone())}</small>
            </span>
          </button>
          <ErrorText text={error} />
          <div className="timezone-results" aria-label="Timezone locations">
            {groups
              .filter(([, items]) => items.length)
              .map(([label, items]) => (
                <section key={label}>
                  <h3>{label}</h3>
                  {items.map((zone) => (
                    <button
                      key={zone}
                      className="timezone-option"
                      aria-pressed={value === zone}
                      disabled={saving || offline}
                      onClick={() => void choose(zone)}
                    >
                      <span>
                        <strong>{timezoneLocation(zone)}</strong>
                        <small>{timezoneOffset(zone)}</small>
                      </span>
                      {value === zone && <Check size={18} />}
                    </button>
                  ))}
                </section>
              ))}
            {!matching.length && (
              <p className="muted">No locations found. Try a city or region.</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
